import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import env from "./env";
// @ts-expect-error no-reason
import { createLink } from "apollo-v3-absinthe-upload-link";
import {
  TAXONOMY_CLIENT,
  COMPANY_SEARCH_CLIENT,
  COMPANY_MATCH_CLIENT,
  COMPANY_SEARCH_UPLOAD_CLIENT,
  HG_FORCE_CLIENT,
  SCORING_CLIENT,
} from "./consts";
import { clearSession } from "./auth";
import { companyMatchRoutingHeaders } from "./companyMatchRouting";

const baseUrl = env.PLATFORM_URL_BASE;

// Create separate links for each API endpoint
const taxonomyLink = new HttpLink({
  uri: `${baseUrl}/taxonomy-api/graphql`,
});

const companySearchLink = new HttpLink({
  uri: `${baseUrl}/company-search-api/graphql`,
});

// Multipart link for company-search-api file uploads (Known Companies). It targets
// /graphql/upload, the only route (nginx + company-search-api) that accepts
// large bodies; every other company-search operation stays on the plain HttpLink.
const companySearchUploadLink = createLink({
  uri: `${baseUrl}/company-search-api/graphql/upload`,
});

const companyMatchLink = createLink({
  uri: `${baseUrl}/company-match-api/graphql`,
});

// Adds the RGIP routing header for users with the rgip-company-match flag on.
const companyMatchRoutingLink = setContext((_, { headers }) => ({
  headers: { ...headers, ...companyMatchRoutingHeaders() },
}));

const overseerLink = new HttpLink({
  uri: `${baseUrl}/overseer/api/platform/graphql`,
});

const hgForceLink = new HttpLink({
  uri: `${baseUrl}/hg-force-api/graphql`,
  credentials: "omit", // Prevent cookies from being sent to avoid large request headers
});

const scoringLink = new HttpLink({
  uri: `${baseUrl}/scoring-api/graphql`,
});

// Auth link to inject the token
const authLink = setContext(async (_, { headers }) => {
  const token = localStorage.getItem("access_token");

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

// Error link to handle authentication errors
const errorLink = onError(({ graphQLErrors, networkError }) => {
  // Handle GraphQL errors
  if (graphQLErrors && Array.isArray(graphQLErrors)) {
    graphQLErrors.forEach(({ message, extensions }) => {
      // Check if it's an authentication error
      if (
        extensions?.code === "UNAUTHENTICATED" ||
        message.includes("Unauthorized")
      ) {
        console.warn("GraphQL authentication error - redirecting to login");
        clearSession();
      }
    });
  }

  // Handle network errors (including 401 HTTP status)
  if (networkError && "statusCode" in networkError) {
    if (networkError.statusCode === 401) {
      console.warn("Network authentication error (401) - redirecting to login");
      clearSession();
    }
  }
});

// Create route link to direct operations to the appropriate endpoint
const routeLink = ApolloLink.split(
  (operation) => operation.getContext().clientName === TAXONOMY_CLIENT,
  taxonomyLink,
  ApolloLink.split(
    (operation) => operation.getContext().clientName === COMPANY_SEARCH_CLIENT,
    companySearchLink,
    ApolloLink.split(
      (operation) =>
        operation.getContext().clientName === COMPANY_SEARCH_UPLOAD_CLIENT,
      companySearchUploadLink,
      ApolloLink.split(
        (operation) =>
          operation.getContext().clientName === COMPANY_MATCH_CLIENT,
        companyMatchRoutingLink.concat(companyMatchLink),
        ApolloLink.split(
          (operation) => operation.getContext().clientName === HG_FORCE_CLIENT,
          hgForceLink,
          ApolloLink.split(
            (operation) => operation.getContext().clientName === SCORING_CLIENT,
            scoringLink,
            overseerLink // Default link
          )
        )
      )
    )
  )
);

// Combine auth, error handling, and routing
const link = authLink.concat(errorLink).concat(routeLink);

export const GraphQLClient = () => {
  return new ApolloClient({
    link,
    cache: new InMemoryCache(),
  });
};
